import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Share2, ArrowRight, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { formatLocalizedDate } from '../utils/formatDate';
import SubscribeInsights from '../components/sections/SubscribeInsights';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LABELS = {
  linkCopied: { en: 'Link copied to clipboard', pt: 'Ligação copiada', fr: 'Lien copié', ar: 'تم نسخ الرابط', zh: '链接已复制' },
  notFoundTitle: { en: 'Article Not Found', pt: 'Artigo não encontrado', fr: 'Article introuvable', ar: 'المقال غير موجود', zh: '未找到文章' },
  notFoundMsg: { en: 'The article you are looking for does not exist or has been moved.', pt: 'O artigo que procura não existe ou foi movido.', fr: "L'article que vous recherchez n'existe pas ou a été déplacé.", ar: 'المقال الذي تبحث عنه غير موجود أو تم نقله.', zh: '您查找的文章不存在或已被移动。' },
  backToInsights: { en: 'Back to Insights', pt: 'Voltar aos Insights', fr: 'Retour aux Insights', ar: 'العودة إلى الرؤى', zh: '返回资讯' },
  allInsights: { en: 'All Insights', pt: 'Todos os Insights', fr: 'Tous les Insights', ar: 'كل الرؤى', zh: '全部资讯' },
  share: { en: 'Share', pt: 'Partilhar', fr: 'Partager', ar: 'مشاركة', zh: '分享' },
  comingSoon: { en: 'Full article content coming soon.', pt: 'Conteúdo completo do artigo em breve.', fr: "Le contenu complet de l'article arrive bientôt.", ar: 'محتوى المقال الكامل قريبًا.', zh: '完整文章内容即将推出。' },
  moreFrom: { en: 'More from Grupo BMP', pt: 'Mais do Grupo BMP', fr: 'Plus de Grupo BMP', ar: 'المزيد من Grupo BMP', zh: '更多来自 Grupo BMP' },
};

/**
 * Resolves the current article by matching the last URL segment of its `link`
 * against the :slug param. Loads from the multilingual insights CMS endpoint.
 */
const ArticleDetailPage = () => {
  const { slug } = useParams();
  const { language } = useLanguage();
  const L = (k) => (LABELS[k] && (LABELS[k][language] || LABELS[k].en)) || '';
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const lang = ['pt', 'en', 'fr', 'ar', 'zh'].includes(language) ? language : 'en';
        const r = await axios.get(`${API_URL}/api/homepage-ml/insights`, { params: { lang } });
        const articles = r.data?.articles || [];
        const found = articles.find(
          (a) => (a.link || '').split('/').filter(Boolean).pop() === slug
        );
        if (!found) {
          setNotFound(true);
        } else {
          setArticle(found);
          setRelated(articles.filter((a) => a.id !== found.id).slice(0, 3));
        }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug, language]);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const handleShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: article?.title, url: shareUrl }); } catch { /* ignore */ }
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(shareUrl);
      alert(L('linkCopied'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00B4D8]" />
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="min-h-screen bg-white pt-32 pb-24">
        <div className="container mx-auto px-6 max-w-2xl text-center">
          <h1 className="text-4xl font-bold text-[#2B2D42] mb-3">{L('notFoundTitle')}</h1>
          <p className="text-[#6C757D] mb-8">
            {L('notFoundMsg')}
          </p>
          <Link to="/insights" className="inline-flex items-center gap-2 text-[#00B4D8] font-semibold hover:underline">
            <ArrowLeft className="w-4 h-4" /> {L('backToInsights')}
          </Link>
        </div>
      </div>
    );
  }

  const body = article.body || '';
  const paragraphs = body.split(/\n\s*\n/).filter(Boolean);

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="article-detail-page">
      {/* Hero (with inline breadcrumb + category for a single cohesive block) */}
      <section className="bg-gradient-to-br from-[#023E8A] via-[#0353A4] to-[#001219] text-white pt-10 pb-20">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-10">
            <Link
              to="/insights"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white transition-colors"
              data-testid="breadcrumb-all-insights"
            >
              <ArrowLeft className="w-4 h-4" /> {L('allInsights')}
            </Link>
            {article.category && (
              <span className="inline-block px-3 py-1.5 bg-[#48CAE4]/15 border border-[#48CAE4]/40 text-[#48CAE4] text-[11px] font-bold uppercase tracking-wider rounded-full backdrop-blur">
                {article.category}
              </span>
            )}
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold leading-[1.1] tracking-tight mb-6">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-5 text-sm text-white/70">
            <span className="flex items-center gap-2"><Calendar className="w-4 h-4" /> {formatLocalizedDate(article.date, language)}</span>
            <button onClick={handleShare} className="flex items-center gap-2 hover:text-white transition-colors" data-testid="share-btn">
              <Share2 className="w-4 h-4" /> {L('share')}
            </button>
          </div>
        </div>
      </section>

      {/* Cover image */}
      {article.image && (
        <div className="container mx-auto px-6 max-w-4xl -mt-8 mb-12">
          <div className="rounded-2xl overflow-hidden shadow-2xl border border-[#E9ECEF] aspect-video bg-[#F1F3F5]">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </div>
        </div>
      )}

      {/* Body */}
      <article className="container mx-auto px-6 max-w-3xl pb-20">
        <p className="text-xl md:text-2xl text-[#2B2D42] font-light leading-relaxed mb-10 border-l-4 border-[#00B4D8] pl-6">
          {article.excerpt}
        </p>

        <div className="prose prose-lg max-w-none text-[#2B2D42] leading-relaxed">
          {paragraphs.length > 0 ? (
            paragraphs.map((p, i) => {
              const trimmed = p.trim();
              // Heading pattern: "## Title"
              if (/^##\s+/.test(trimmed)) {
                return (
                  <h2 key={i} className="text-2xl md:text-3xl font-bold text-[#2B2D42] mt-10 mb-4">
                    {trimmed.replace(/^##\s+/, '')}
                  </h2>
                );
              }
              if (/^-\s+/m.test(trimmed)) {
                const items = trimmed.split(/\n-\s+/).map((s) => s.replace(/^-\s+/, '')).filter(Boolean);
                return (
                  <ul key={i} className="list-disc pl-6 space-y-2 my-6 text-[#2B2D42]">
                    {items.map((li, j) => <li key={j}>{li}</li>)}
                  </ul>
                );
              }
              return (
                <p key={i} className="mb-5 text-base md:text-lg text-[#2B2D42] leading-[1.8]">
                  {trimmed}
                </p>
              );
            })
          ) : (
            <p className="text-[#6C757D] italic">{L('comingSoon')}</p>
          )}
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-[#F8F9FA] py-16">
          <div className="container mx-auto px-6 max-w-6xl">
            <h3 className="text-2xl md:text-3xl font-bold text-[#2B2D42] mb-8">{L('moreFrom')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((a) => {
                const slug2 = (a.link || '').split('/').filter(Boolean).pop();
                return (
                  <Link key={a.id} to={`/insights/${slug2}`} className="group bg-white border border-[#E9ECEF] rounded-xl overflow-hidden hover:border-[#00B4D8] hover:shadow-lg transition-all">
                    {a.image && (
                      <div className="aspect-video bg-[#F1F3F5] overflow-hidden">
                        <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      </div>
                    )}
                    <div className="p-5">
                      {a.category && (
                        <span className="inline-block px-2 py-1 bg-[#00B4D8]/10 text-[#00B4D8] text-[10px] font-semibold uppercase rounded mb-2">
                          {a.category}
                        </span>
                      )}
                      <h4 className="text-base font-semibold text-[#2B2D42] line-clamp-2 group-hover:text-[#00B4D8] transition-colors">
                        {a.title}
                      </h4>
                      <span className="text-xs text-[#ADB5BD] mt-2 block">{formatLocalizedDate(a.date, language)}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Subscribe CTA (replaces generic contact CTA at end of article) */}
      <SubscribeInsights articleSlug={slug} language={language} />
    </div>
  );
};

export default ArticleDetailPage;
